import React, { useEffect } from 'react';
import { useModalQueue } from './ModalQueueContext';

interface IUseModalOptions {
  id: string;
  condition?: boolean;
}

export const useModal = ({ id, condition = true }: IUseModalOptions) => {
  const { unregisterModal, showModal, hideModal, isModalActive } = useModalQueue();

  // Unregister the modal when unmounting
  useEffect(() => {
    if (condition) {
      return () => {
        unregisterModal(id);
      };
    }

    return undefined;
  }, [id, unregisterModal, condition]);

  // Use useEffect with a ref to prevent infinite re-renders
  const initialRenderRef = React.useRef(true);

  useEffect(() => {
    // Skip the effect on the very first render to avoid looping
    if (initialRenderRef.current) {
      initialRenderRef.current = false;

      // Only show modal on initial render if condition is true
      if (condition) {
        showModal(id);
      }
      return;
    }

    // After initial render, handle condition changes normally
    if (condition) {
      showModal(id);
    } else {
      hideModal(id);
    }
  }, [condition, hideModal, id, showModal]);

  return {
    isActive: isModalActive(id),
    show: () => showModal(id),
    hide: () => hideModal(id),
  };
};
